import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import Modal from '../Modal';
import DayMealsPopup from './DayMealsPopup';
import MealIdeaPanel from '../mealIdeas/MealIdeaPanel';
import { addMealIdeaToDate, convertMealName, createMealIdeaOnDate, fetchCommonMeals, loadMealIdeas } from '../../../redux/actions/mealIdeas';
import { addItemToDate, deleteMeal, saveCalendarEvent, setSlotNote } from '../../../redux/actions/calendar';
import { useBoughtListItemActions } from './BoughtListItems';
import { mealKey } from '../../../redux/food/normalize';
import {
	selectBoughtListsByDay,
	selectCalendarEvents,
	selectDayEvents,
	selectGroceryItemCatalog,
	selectMealIdeasIndex,
	selectMealIdeasLoaded,
	selectMealIdeasLoading,
} from '../../../redux/food/selectors';
import useOffline from '../../hooks/useOffline';

const NO_LISTS = [];

/**
 * DayMealsPopup in a modal, wired to the store.
 *
 * The day's meals and slot notes are the food store's (selectDayEvents). Adding, removing and a slot's
 * note are ops (actions/calendar.js), kept at once and sent when there is a connection; making a new
 * meal idea, or a meal's name into one, is online-only, and blocks the popup while it is saved; so
 * are the day's events. Offline, those are disabled (DayMealsPopup's `offline`).
 *
 * A meal's idea opens for editing in a second modal over this one, so closing it lands back on the
 * slot it was opened from. Removing the meal from its slot is done there, and closes it.
 *
 * `origin` is the element the day popup grows out of; see Modal.
 *
 * What a slot most often holds is loaded as the slot opens, once per slot; failing leaves the search
 * working without suggestions. The budget's grocery items are the food store's catalog, shown as
 * loading while the store has nothing yet, and as none when it could get nothing.
 */
export default function DayMealsModal({ date, origin = null, onClose }) {
	const dispatch = useDispatch();
	const store = useStore();
	const day = date.format('YYYY-MM-DD');
	const dayEvent = useSelector((state) => selectDayEvents(day, day)(state.food.entities)[day] || null);
	const calendarEvents = useSelector((state) => selectCalendarEvents(day, day)(state.food.entities));
	const groceryLists = useSelector((state) => selectBoughtListsByDay(day, day)(state.food.entities)[day] || NO_LISTS);
	const ideasById = useSelector((state) => selectMealIdeasIndex(state).ideasById);
	const ideasLoaded = useSelector(selectMealIdeasLoaded);
	const ideasLoading = useSelector(selectMealIdeasLoading);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [notice, setNotice] = useState(null);
	// { id, slot, meal }: the idea being edited, and the meal on this day it was opened from.
	const [openIdea, setOpenIdea] = useState(null);
	const idea = openIdea ? ideasById[openIdea.id] || null : null;
	const closeIdea = () => setOpenIdea(null);
	const [commonBySlot, setCommonBySlot] = useState({});
	const groceryItems = useSelector((state) => (state.food.status === 'empty' ? null : selectGroceryItemCatalog(state.food.entities)));
	const requested = useRef({ slots: new Set() });
	const boughtItemActions = useBoughtListItemActions();
	const offline = useOffline();

	const loadCommon = (type) => {
		if(requested.current.slots.has(type)) {
			return;
		}
		requested.current.slots.add(type);
		fetchCommonMeals(date, type)
			.then((common) => setCommonBySlot((loaded) => ({ ...loaded, [type]: common })))
			.catch(() => setCommonBySlot((loaded) => ({ ...loaded, [type]: { meal_idea_ids: [], names: [] } })));
	};

	useEffect(() => {
		if(!ideasLoaded && !ideasLoading) {
			dispatch(loadMealIdeas());
		}
	}, []);

	const ideas = useMemo(() => Object.values(ideasById), [ideasById]);

	// Resolves whether the change was made. A meal ideas error is shown only when this change set it,
	// not one left over from something else.
	const save = (thunk) => {
		const errorBefore = store.getState().mealIdeas.error;
		setSaving(true);
		setError(null);
		return dispatch(thunk).then((saved) => {
			setSaving(false);
			if(!saved) {
				const ideaError = store.getState().mealIdeas.error;
				setError((ideaError !== errorBefore && ideaError) || 'The day could not be saved. Try again.');
			}
			return saved;
		});
	};
	// The idea opens straight away, for its ingredients; the count says the other days went with it.
	const convertMeal = (slot, meal, options) => {
		setNotice(null);
		return save(convertMealName(date, meal.name, options)).then((result) => {
			if(result) {
				const meals = result.mealsLinked === 1 ? '1 meal' : `${result.mealsLinked} meals`;
				const rows = result.rowsLinked === 0 ? '' :
					` and ${result.rowsLinked === 1 ? '1 bought item' : `${result.rowsLinked} bought items`}`;
				setNotice(`${meals}${rows} now use this meal idea.`);
				setOpenIdea({ id: result.mealIdeaId, slot, meal });
			}
			return result;
		});
	};
	// A note saves when its field loses focus, which a tap on a meal idea does as well. Being busy would
	// disable the idea between the press and the click, so the tap saved the note and added nothing.
	const saveNote = (type, notes) => dispatch(setSlotNote(date, type, notes)).then((saved) => {
		if(!saved) {
			setError('The note could not be saved. Try again.');
		}
		return saved;
	});

	return (
		<>
			<Modal
				className='day-meals-modal'
				header={date.format('dddd, LL')}
				origin={origin}
				onClose={onClose}
			>
				<DayMealsPopup
					dayEvent={dayEvent}
					date={date}
					mealIdeas={ideas}
					busy={saving}
					error={error}
					onAddIdea={(type, idea) => save(addMealIdeaToDate(date, type, idea))}
					onCreateIdea={(type, name) => save(createMealIdeaOnDate(date, type, name))}
					onAddItem={(type, name) => save(addItemToDate(date, type, name))}
					commonBySlot={commonBySlot}
					onOpenSlot={loadCommon}
					groceryItems={groceryItems}
					onRemoveMeal={(type, meal) => save(deleteMeal(mealKey(meal)))}
					onConvertMeal={convertMeal}
					notice={notice}
					onSaveSlotNote={saveNote}
					onOpenMeal={(slot, meal, picked) => setOpenIdea({ id: picked.id, slot, meal })}
					boughtItemActions={boughtItemActions}
					calendarEvents={calendarEvents}
					groceryLists={groceryLists}
					offline={offline}
					onSaveEvent={(event) => {
						setSaving(true);
						return dispatch(saveCalendarEvent(event)).then((saved) => {
							setSaving(false);
							return saved;
						});
					}}
				/>
			</Modal>
			{!!idea &&
			<Modal className='meal_idea_modal' onClose={closeIdea}>
				<MealIdeaPanel
					key={idea.id}
					idea={idea}
					onClose={closeIdea}
					removal={{
						meal: openIdea.meal,
						slot: openIdea.slot,
						onRemove: () => save(deleteMeal(mealKey(openIdea.meal))).then((removed) => {
							if(removed) {
								closeIdea();
							}
							return removed;
						}),
					}}
				/>
			</Modal>
			}
		</>
	);
}
